<template>
  <div class="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-4 dark:border-slate-700 dark:bg-slate-800/60">
    <p v-if="!compact" class="text-sm font-medium text-[#182233] dark:text-slate-100">
      Complete the human verification to continue.
    </p>
    <div ref="containerRef" :class="compact ? '' : 'mt-3'" class="min-h-17.5" />
    <p
      v-if="statusMessage"
      class="mt-2 text-xs font-medium"
      :class="statusTone === 'error' ? 'text-red-600 dark:text-red-300' : 'text-green-700 dark:text-green-300'"
    >
      {{ statusMessage }}
    </p>
  </div>
</template>

<script setup lang="ts">
type TurnstileRenderOptions = {
  sitekey: string;
  action?: string;
  callback?: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string | number;
  reset: (widgetId: string | number) => void;
  remove?: (widgetId: string | number) => void;
};

type BrowserWindowWithTurnstile = Window & typeof globalThis & {
  turnstile?: TurnstileApi;
  __beesphoneTurnstileLoader?: Promise<TurnstileApi>;
};

const props = withDefaults(defineProps<{
  action?: string;
  compact?: boolean;
}>(), {
  action: "signup",
  compact: false,
});

const emit = defineEmits<{
  verified: [token: string];
  expired: [];
  error: [message: string];
}>();

const runtimeConfig = useRuntimeConfig();
const siteKey = runtimeConfig.public.turnstileSiteKey || "";
const containerRef = ref<HTMLElement | null>(null);
const statusMessage = ref(siteKey ? "" : "Human verification is unavailable right now.");
const statusTone = ref<"success" | "error">("error");

let widgetId: string | number | null = null;

const emitError = (message: string) => {
  statusTone.value = "error";
  statusMessage.value = message;
  emit("error", message);
};

const loadTurnstile = async (): Promise<TurnstileApi> => {
  const browserWindow = window as BrowserWindowWithTurnstile;
  if (browserWindow.turnstile) {
    return browserWindow.turnstile;
  }

  if (!browserWindow.__beesphoneTurnstileLoader) {
    browserWindow.__beesphoneTurnstileLoader = new Promise<TurnstileApi>((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>(
        'script[data-beesphone-turnstile="true"]',
      );

      if (existingScript) {
        existingScript.addEventListener("load", () => {
          if (browserWindow.turnstile) {
            resolve(browserWindow.turnstile);
            return;
          }

          reject(new Error("Turnstile did not initialize."));
        }, { once: true });
        existingScript.addEventListener("error", () => {
          reject(new Error("Turnstile failed to load."));
        }, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.beesphoneTurnstile = "true";
      script.onload = () => {
        if (browserWindow.turnstile) {
          resolve(browserWindow.turnstile);
          return;
        }

        reject(new Error("Turnstile did not initialize."));
      };
      script.onerror = () => reject(new Error("Turnstile failed to load."));
      document.head.appendChild(script);
    });
  }

  return browserWindow.__beesphoneTurnstileLoader;
};

onMounted(async () => {
  if (!siteKey) {
    emitError("Human verification is unavailable right now.");
    return;
  }

  try {
    const turnstile = await loadTurnstile();
    if (!containerRef.value) {
      emitError("Human verification could not be displayed.");
      return;
    }

    widgetId = turnstile.render(containerRef.value, {
      sitekey: siteKey,
      action: props.action,
      callback: (token: string) => {
        statusTone.value = "success";
        statusMessage.value = "Verification complete.";
        emit("verified", token);
      },
      "expired-callback": () => {
        statusTone.value = "error";
        statusMessage.value = "Verification expired. Please complete the check again.";
        emit("expired");
        if (widgetId != null) {
          turnstile.reset(widgetId);
        }
      },
      "error-callback": () => {
        emitError("Human verification could not be completed. Please try again.");
      },
    });
  } catch {
    emitError("Human verification could not be loaded right now.");
  }
});

onBeforeUnmount(() => {
  if (!import.meta.client) {
    return;
  }

  const browserWindow = window as BrowserWindowWithTurnstile;
  if (widgetId == null || !browserWindow.turnstile?.remove) {
    return;
  }

  try {
    browserWindow.turnstile.remove(widgetId);
  } catch {
    // Ignore cleanup failures from the third-party widget.
  }
});
</script>